import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import UserBadge from '@/features/user-profile/components/UserBadge.vue';
import TextRecommendationCardModal from './TextRecommendationCardModal.vue';
import TextRecommendationModal from './TextRecommendationModal.vue';
import Popover from '@/ui-components/Popover.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { toastError, toastSuccess } from '@/helpers/toast/toast-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { useMutation } from '@/helpers/composables/useApollo';
import {
	DeleteTextRecommendationDocument,
	DeleteTextRecommendationMutation,
	DeleteTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/DeleteTextRecommendation.mutation';
import { isMobile } from '@/helpers/ui-helper';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';
import { AccountModes } from '@/enums/login';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { ShareMethod } from '@/enums/share-methods';
import { useRoute } from 'vue-router/composables';
import { useTextRecommendation } from '@/helpers/composables/useTextRecommendation';
import { useMounted } from '@vueuse/core';


export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationCard',
  props: {
    textRecommendation: null,
    title: null,
    isSingleTextRecommendation: { type: Boolean },
    additionalContexts: null
  },
  setup(__props: any) {

const props = __props as {
	textRecommendation: TextRecommendationFragment;
	title: TitleDetail;
	isSingleTextRecommendation?: boolean;
	additionalContexts: SnowplowContext[];
};



const { language, country } = useLanguageStore();
const isMounted = useMounted();

const {
	hasTags,
	likeRecommendationMutation,
	likedCount,
	likedByUser,
	profile,
	shareId,
	shareRecommendation,
	trackShareClicked,
	tagsString,
	watchedOnText,
	ownedByUser,
} = useTextRecommendation({
	textRecommendation: () => props.textRecommendation,
	title: () => props.title,
});

const displayCardModal = ref(false);
const displayTextRecommendationModal = ref<boolean>(false);
const showOriginalText = ref<boolean>(false);

const headline = computed(() => props.textRecommendation.headline);
const originalHeadline = computed(() => props.textRecommendation.originalHeadline);
const body = computed(() => props.textRecommendation.body);
const originalBody = computed(() => props.textRecommendation.originalBody);

const displayedHeadline = computed(() =>
	showOriginalText.value && originalHeadline.value ? originalHeadline.value : headline.value
);

const displayedBody = computed(() => (showOriginalText.value && originalBody.value ? originalBody.value : body.value));

const backgroundImageUrl = computed(() => JW_CONFIG.IMAGESCALER_URL + props.title.content.fullPosterUrl);

const formData = computed(() => {
	return {
		__typename: 'TextRecommendation' as const,
		id: props.textRecommendation.id,
		headline: headline.value,
		body: body.value,
		tags: props.textRecommendation.tags,
		watchedOn: props.textRecommendation.watchedOn,
		watchedAt: props.textRecommendation.watchedAt,
		profile: props.textRecommendation.profile,
		ownedByUser: props.textRecommendation.ownedByUser,
		likeCount: likedCount.value,
		likedByUser: likedByUser.value,
	};
});

const isMobileViewport = computed(() => isMounted.value && isMobile());

const displayReadMore = computed(() => {
	if (props.isSingleTextRecommendation) return false;

	const bodyLength = body.value?.length ?? 0;

	if (hasTags.value) {
		return !!((isMobileViewport.value && bodyLength > 410) || bodyLength > 505);
	} else {
		return !!((isMobileViewport.value && bodyLength > 500) || bodyLength > 560);
	}
});

const isTranslated = computed(() => {
	return !!(props.textRecommendation.originalHeadline || props.textRecommendation.originalBody);
});

function toggleTextRecommendationModal(value: boolean) {
	if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	displayTextRecommendationModal.value = value;

	displayTextRecommendationModal.value ? disableScroll() : enableScroll();
}

function handleProfileCLick() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'author_clicked',
			useLastRoute: true,
		},
		props.additionalContexts
	);
}

function handleLikeClick() {
	const likeValue = !likedByUser.value;

	likeRecommendationMutation({
		input: {
			uuid: props.textRecommendation.id,
			like: likeValue,
		},
		country: country.value,
		language: language.value,
	});

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'helpful_clicked',
			value: likeValue ? 1 : 0,
		},
		props.additionalContexts
	);
}

function handleShowTranslation() {
	showOriginalText.value = !showOriginalText.value;
}

const deleteRecommendation = useMutation<DeleteTextRecommendationMutation, DeleteTextRecommendationMutationVariables>(
	DeleteTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

function deleteTextRecommendation() {
	deleteRecommendation.mutate({
		input: {
			id: props.textRecommendation.id,
		},
	});
}

deleteRecommendation.onDone(() => {
	toastSuccess({
		message: 'Recommendation deleted',
		duration: 3000,
	});
});

deleteRecommendation.onError(() => {
	toastError({
		message: 'Failed to delete recommendation',
		duration: 3000,
	});
});

function toggleCardModal(display: boolean) {
	displayCardModal.value = display;

	if (displayCardModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'read_more_clicked',
			},
			props.additionalContexts
		);
	}

	displayCardModal.value ? disableScroll() : enableScroll();
}

/* SHARING */
const shareIcon = `/${ASSETS_DIR}/img/share.svg`;

const route = useRoute();
const fromRecShare = ref(false);

onMounted(() => {
	if (route.hash !== `#${shareId.value}`) return;

	// Waiting a tiny bit because anchor links work right away but the page content
	// is still rendering and shifting (on mounted renders like ads, experiments, etc.)
	// This helps improve the experience.
	setTimeout(() => {
		// TextRecommendations.vue has both mobile & desktop versions of the card in the HTML
		// They're hidden with CSS and can't be selected so I select them all and check visibility
		const elements = [...document.querySelectorAll(route.hash)];
		const element = elements.find(e => e != null && e.checkVisibility());
		if (element != null) {
			element?.scrollIntoView();
			fromRecShare.value = true;
		}
	}, 250);
});

return { __sfc: true,props, language, country, isMounted, hasTags, likeRecommendationMutation, likedCount, likedByUser, profile, shareId, shareRecommendation, trackShareClicked, tagsString, watchedOnText, ownedByUser, displayCardModal, displayTextRecommendationModal, showOriginalText, headline, originalHeadline, body, originalBody, displayedHeadline, displayedBody, backgroundImageUrl, formData, isMobileViewport, displayReadMore, isTranslated, toggleTextRecommendationModal, handleProfileCLick, handleLikeClick, handleShowTranslation, deleteRecommendation, deleteTextRecommendation, toggleCardModal, shareIcon, route, fromRecShare, UserBadge, TextRecommendationCardModal, TextRecommendationModal, Popover, FontAwesomeIcon, ShareMethod }
}

})